import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { NormalizedOrder } from '../../../../pages/Dispatch/lib/normalizeOrder'
import type { ColumnSort } from '../../../../pages/Dispatch/lib/columnSort'
import {
  query,
  where,
  onSnapshot,
  writeBatch,
  doc,
  updateDoc,
} from 'firebase/firestore'
import { getProjectCollection, db } from '../../../../services/firebase/firebaseConfig'
import { listenerRegistry } from '../../../../pages/Dispatch/lib/listenerRegistry'
import { normalizeOrder } from '../../../../pages/Dispatch/lib/normalizeOrder'
import { formatClientName } from '../../../../pages/Dispatch/lib/orderDisplay'
import {
  LIFECYCLES,
  DRIVER_CONTROLLED_LIFECYCLES,
  isContractLifecycle,
} from '../../../../pages/Dispatch/lib/lifecycles'
import { selectRegions, selectSubstatusMaps, selectRegionDriversDashboard } from './selectors'
import { resolveLinkedGroups } from '../../../../components/OrderDetailsTabs/components/createUpdate/components/linked-order/tmsLinkRefs'
import { setLinkedGroups } from '../linkedGroupsSlice'

export interface DashboardFilters {
  vehicles: string[]
  drivers: string[]
  clients: string[]
  lifecycles: string[]
  vehicleTypes: string[]
  serviceTypes: string[]
  search: string
}

/** A live "new order arrived" notice, surfaced as a toast in Today mode. */
export interface NewOrderNotice {
  id: string
  invoice: string
  client: string
}

export interface DriverLocation {
  vehicle: string
  driverUserName: string
  driverFullName: string
  location: { lat: number; lng: number; timestamp: number }
  lastUpdated: number
}

export interface DashboardState {
  // date/region scoping for the live listener
  dateMode: 'today' | 'range'
  dateRange: { from: string | null; to: string | null } // ISO strings — serializable
  region: string
  // live operational data
  orders: NormalizedOrder[]
  driverLocations: Record<string, DriverLocation>
  // UI state
  filters: DashboardFilters
  density: 'detailed' | 'compact'
  expandedPanel: 'kanban' | 'map' | null
  columnMode: 'lifecycle' | 'driver' | 'vehicle'
  selectedOrderId: string | null
  bulkSelection: string[]
  columnVisibility: Record<string, boolean>
  lifecycleOrder: string[]
  driverColumnVisibility: Record<string, boolean>
  driverColumnOrder: string[]
  vehicleColumnVisibility: Record<string, boolean>
  vehicleColumnOrder: string[]
  /** Per-column sort, lifecycle mode only. Absent key = StartWindow ascending. */
  columnSort: Record<string, ColumnSort>
  /** Live "new order arrived" toasts (Today mode). */
  newOrderNotices: NewOrderNotice[]
  /** Nonce-tagged request to center the map on an order (from a notice's "View"). */
  centerRequest: { orderId: string; nonce: number } | null
  loading: { orders: boolean }
  error: string | null
}

export const initialDashboardState: DashboardState = {
  dateMode: 'today',
  dateRange: { from: null, to: null },
  region: '',
  orders: [],
  driverLocations: {},
  filters: { vehicles: [], drivers: [], clients: [], lifecycles: [], vehicleTypes: [], serviceTypes: [], search: '' },
  density: 'detailed',
  expandedPanel: null,
  columnMode: 'lifecycle',
  selectedOrderId: null,
  bulkSelection: [],
  columnVisibility: {},
  lifecycleOrder: [],
  driverColumnVisibility: {},
  driverColumnOrder: [],
  vehicleColumnVisibility: {},
  vehicleColumnOrder: [],
  columnSort: {},
  newOrderNotices: [],
  centerRequest: null,
  loading: { orders: false },
  error: null,
}

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: initialDashboardState,
  reducers: {
    setDateMode(state, action: PayloadAction<DashboardState['dateMode']>) {
      state.dateMode = action.payload
    },
    setDateRange(state, action: PayloadAction<DashboardState['dateRange']>) {
      state.dateRange = action.payload
    },
    setRegion(state, action: PayloadAction<string>) {
      state.region = action.payload
    },
    setFilters(state, action: PayloadAction<DashboardFilters>) {
      state.filters = action.payload
    },
    setDensity(state, action: PayloadAction<DashboardState['density']>) {
      state.density = action.payload
    },
    setExpandedPanel(state, action: PayloadAction<DashboardState['expandedPanel']>) {
      state.expandedPanel = action.payload
    },
    setColumnMode(state, action: PayloadAction<DashboardState['columnMode']>) {
      state.columnMode = action.payload
    },
    setSelectedOrder(state, action: PayloadAction<string | null>) {
      state.selectedOrderId = action.payload
    },
    setBulkSelection(state, action: PayloadAction<string[]>) {
      state.bulkSelection = Array.isArray(action.payload)
        ? Array.from(new Set(action.payload))
        : []
    },
    setColumnVisibility(state, action: PayloadAction<Record<string, boolean>>) {
      state.columnVisibility = action.payload
    },
    setLifecycleOrder(state, action: PayloadAction<string[]>) {
      state.lifecycleOrder = action.payload
    },
    setDriverColumnVisibility(state, action: PayloadAction<Record<string, boolean>>) {
      state.driverColumnVisibility = action.payload
    },
    setDriverColumnOrder(state, action: PayloadAction<string[]>) {
      state.driverColumnOrder = action.payload
    },
    setVehicleColumnVisibility(state, action: PayloadAction<Record<string, boolean>>) {
      state.vehicleColumnVisibility = action.payload
    },
    setVehicleColumnOrder(state, action: PayloadAction<string[]>) {
      state.vehicleColumnOrder = action.payload
    },
    setColumnSort(
      state,
      action: PayloadAction<{ columnName: string; sort: ColumnSort | null }>,
    ) {
      const { columnName, sort } = action.payload
      // null clears back to the default (StartWindow asc) rather than storing it.
      if (sort === null) {
        delete state.columnSort[columnName]
      } else {
        state.columnSort[columnName] = sort
      }
    },
    setOrders(state, action: PayloadAction<NormalizedOrder[]>) {
      state.orders = action.payload
    },
    setDriverLocation(
      state,
      action: PayloadAction<{
        driverId: string
        vehicle: string
        driverUserName: string
        driverFullName: string
        location: { lat: number; lng: number; timestamp: number }
      }>,
    ) {
      const { driverId, vehicle, driverUserName, driverFullName, location } = action.payload
      state.driverLocations[driverId] = {
        vehicle,
        driverUserName,
        driverFullName,
        location,
        lastUpdated: Date.now(),
      }
    },
    resetDriverLocations(state) {
      state.driverLocations = {}
    },
    setLoading(state, action: PayloadAction<Partial<DashboardState['loading']>>) {
      state.loading = { ...state.loading, ...action.payload }
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
    },
    pushNewOrderNotices(state, action: PayloadAction<NewOrderNotice[]>) {
      // Dedupe by order number (fields.Invoice) — against existing notices AND
      // within this batch — so the same order can't stack multiple toasts.
      const seen = new Set(state.newOrderNotices.map((n) => n.invoice))
      const fresh: NewOrderNotice[] = []
      for (const n of action.payload) {
        if (seen.has(n.invoice)) continue
        seen.add(n.invoice)
        fresh.push(n)
      }
      // Newest first; cap the stack so a burst can't pile up unbounded.
      state.newOrderNotices = [...fresh, ...state.newOrderNotices].slice(0, 6)
    },
    dismissNewOrderNotice(state, action: PayloadAction<string>) {
      state.newOrderNotices = state.newOrderNotices.filter((n) => n.id !== action.payload)
    },
    clearNewOrderNotices(state) {
      state.newOrderNotices = []
    },
    requestCenterOnOrder(state, action: PayloadAction<string>) {
      state.centerRequest = { orderId: action.payload, nonce: (state.centerRequest?.nonce ?? 0) + 1 }
    },
  },
})

export const {
  setDateMode,
  setDateRange,
  setRegion,
  setFilters,
  setDensity,
  setExpandedPanel,
  setColumnMode,
  setSelectedOrder,
  setBulkSelection,
  setColumnVisibility,
  setLifecycleOrder,
  setDriverColumnVisibility,
  setDriverColumnOrder,
  setVehicleColumnVisibility,
  setVehicleColumnOrder,
  setColumnSort,
  setOrders,
  setDriverLocation,
  resetDriverLocations,
  setLoading,
  setError,
  pushNewOrderNotices,
  dismissNewOrderNotice,
  clearNewOrderNotices,
  requestCenterOnOrder,
} = dashboardSlice.actions

export default dashboardSlice.reducer

// --- Thunks ------------------------------------------------------------------
type AppDispatch = (action: any) => any
type GetState = () => any

/**
 * Monotonically-increasing generation token. Incremented on every
 * subscribeToOrders call so that stale in-flight resolveLinkedGroups
 * promises (from a previous date/region) cannot overwrite a newer result.
 */
let _ordersGen = 0

/** Compute the Firestore query bounds for the current date mode. */
export function computeRangeBounds(
  dateMode: DashboardState['dateMode'],
  dateRange: DashboardState['dateRange'],
): { rangeStart: Date; rangeEnd: Date } {
  if (dateMode === 'today') {
    const start = new Date()
    start.setHours(0, 0, 0, 0)
    const end = new Date()
    end.setHours(23, 59, 59, 999)
    return { rangeStart: start, rangeEnd: end }
  }
  const { from, to } = dateRange
  if (!from || !to) {
    const fallbackStart = new Date()
    fallbackStart.setHours(0, 0, 0, 0)
    const fallbackEnd = new Date()
    fallbackEnd.setHours(0, 0, 0, 0)
    return { rangeStart: fallbackStart, rangeEnd: fallbackEnd }
  }
  // Use replace to parse ISO dates as local time (not UTC) to avoid midnight-shift on date boundary.
  const rangeStart = new Date(from.replace(/-/g, '/'))
  rangeStart.setHours(0, 0, 0, 0)
  const rangeEnd = new Date(to.replace(/-/g, '/'))
  rangeEnd.setHours(23, 59, 59, 999)
  return { rangeStart, rangeEnd }
}

/** (Re)subscribe the live TMS_ORDERS listener for the current date/region scope. */
export const subscribeToOrders =
  (opts: { showLoading?: boolean } = {}) => (dispatch: AppDispatch, getState: GetState): void => {
    listenerRegistry.stopOrders()

    const state = getState()
    const dash: DashboardState = state.dashboard
    if (!dash.region) return // can't query without a region

    // Notices are scoped to the current date/region view — drop any stale ones.
    // We announce a Delivery order once it receives an order number (see below);
    // gated to Today mode and skipped on the first snapshot.
    dispatch(clearNewOrderNotices())
    const announceNewOrders = dash.dateMode === 'today'
    let firstSnapshot = true

    const regionName =
      selectRegions(state).find((r) => r.id === dash.region)?.name || dash.region
    const { rangeStart, rangeEnd } = computeRangeBounds(dash.dateMode, dash.dateRange)

    // Surface the board spinner whenever the incoming dataset differs from what's on
    // screen (page enter, date-mode switch, date-range change). Region switches
    // re-subscribe silently so a region pick doesn't flash a loader.
    if (opts.showLoading) dispatch(setLoading({ orders: true }))

    // Bump the generation so any in-flight resolves from the previous
    // date/region are dropped when they eventually settle.
    const myGen = ++_ordersGen

    const q = query(
      getProjectCollection('TMS_ORDERS'),
      where('fields.Region', '==', regionName),
      where('fields.StartWindow', '>=', rangeStart),
      where('fields.StartWindow', '<=', rangeEnd),
    )

    const unsub = onSnapshot(
      q,
      (snap: any) => {
        // Previous raw fields.Invoice per order id — read BEFORE setOrders so we can
        // detect an order that just received its order number.
        const prevInvoiceById = new Map<string, any>(
          (getState().dashboard.orders as NormalizedOrder[]).map(
            (o) => [o.id, (o as any).raw?.fields?.Invoice],
          ),
        )

        const orders: NormalizedOrder[] = snap.docs.map((d: any) => normalizeOrder(d))
        dispatch(setOrders(orders))
        dispatch(setLoading({ orders: false }))
        dispatch(setError(null))

        // Announce a Delivery order the moment it gets an order number: a MODIFIED
        // doc whose fields.Invoice went from null/empty → a value. Today mode only,
        // after the first snapshot, and excluding this client's own pending writes.
        if (announceNewOrders && !firstSnapshot) {
          const notices: NewOrderNotice[] = []
          snap.docChanges().forEach((c: any) => {
            if (c.type !== 'modified' || c.doc?.metadata?.hasPendingWrites) return
            const f = c.doc.data()?.fields ?? {}
            if (String(f.OrderType ?? '').replace('clearD - ', '') !== 'Delivery') return
            const newInvoice = f.Invoice
            if (newInvoice === null || newInvoice === undefined || newInvoice === '') return
            const prevInvoice = prevInvoiceById.get(c.doc.id)
            const wasEmpty = prevInvoice === null || prevInvoice === undefined || prevInvoice === ''
            if (!wasEmpty) return
            const o = normalizeOrder(c.doc)
            notices.push({ id: o.id, invoice: o.invoice, client: formatClientName(o.client) })
          })
          if (notices.length) dispatch(pushNewOrderNotices(notices))
        }
        firstSnapshot = false

        // Fire-and-forget: resolve linked-group ref docs for all orders that
        // carry a string LinkedOrders ref (new schema). Mirrors the OrderTable
        // pattern (index.tsx ~lines 1329-1340). A generation guard ensures a
        // stale resolve from a previous subscription does not overwrite a newer one.
        const refIds = orders
          .map((o) => o.linkedRef)
          .filter((r): r is string => typeof r === 'string' && !!r)
        if (refIds.length > 0) {
          const capturedGen = myGen
          resolveLinkedGroups(refIds)
            .then((groupMap) => {
              if (_ordersGen !== capturedGen) return // stale — discard
              if (groupMap && Object.keys(groupMap).length > 0) {
                dispatch(setLinkedGroups(groupMap))
              }
            })
            .catch((e) => console.warn('[dashboard] Failed to resolve linked groups:', e))
        }
      },
      (err: any) => {
        dispatch(setError(err?.message ?? 'Failed to load orders'))
        dispatch(setLoading({ orders: false }))
      },
    )
    listenerRegistry.startOrders(unsub)
  }

/** Set the active region and re-subscribe the orders listener. */
export const setRegionAndResubscribe =
  (region: string) => (dispatch: AppDispatch): void => {
    dispatch(setRegion(region))
    dispatch(subscribeToOrders())
  }

/** Switch date mode and re-subscribe the orders listener. */
export const setDateModeAndResubscribe =
  (mode: DashboardState['dateMode']) => (dispatch: AppDispatch): void => {
    // The selection belongs to the mode being left — those orders aren't in the
    // incoming dataset. BulkSelectionBar renders in every mode, so a stale bulk
    // selection would keep offering reassign / push-to-driver against orders the
    // board is no longer showing.
    dispatch(setSelectedOrder(null))
    dispatch(setBulkSelection([]))
    dispatch(setDateMode(mode))
    dispatch(subscribeToOrders({ showLoading: true }))
  }

/** Set the date range and re-subscribe the orders listener (shows the board spinner). */
export const setDateRangeAndResubscribe =
  (range: DashboardState['dateRange']) => (dispatch: AppDispatch): void => {
    dispatch(setDateRange(range))
    dispatch(subscribeToOrders({ showLoading: true }))
  }

/** Page-enter: set the default region and start the orders listener. */
export const enterDashboard =
  (region: string) => (dispatch: AppDispatch): void => {
    dispatch(setRegion(region))
    // The first snapshot can take a second or two; show the board spinner rather
    // than an empty kanban that looks like "no orders today".
    dispatch(subscribeToOrders({ showLoading: true }))
  }

/** Page-leave: tear down all listeners and clear live state. */
export const leaveDashboard = () => (dispatch: AppDispatch): void => {
  listenerRegistry.stopOrders()
  listenerRegistry.stopAllDrivers()
  dispatch(setOrders([]))
  dispatch(setSelectedOrder(null))
  dispatch(resetDriverLocations())
  dispatch(clearNewOrderNotices())
}

type PromptModal = (
  kind: 'assignVehicle' | 'completeOrCancel' | 'overrideDriver',
  order: any,
  context?: any,
) => Promise<any | null>

/**
 * Central drag handler. Validates the drop target, runs any required modal via
 * the supplied `promptModal` callback, builds the patch per the drag rules and
 * writes it in a Firestore batch. On failure, sets an error and clones the
 * orders array so the optimistic move reverts.
 */
export const transitionOrder =
  (payload: {
    orderId: string
    toLifeCycle: string
    promptModal: PromptModal
    priorStop?: any
  }) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const state = getState()
    const dash: DashboardState = state.dashboard
    const order = dash.orders.find((o) => o.id === payload.orderId)
    if (!order) return

    const toLc = payload.toLifeCycle
    if (!isContractLifecycle(toLc)) {
      dispatch(setError(`No drag rule defined for "${toLc}".`))
      dispatch(setOrders(dash.orders.slice()))
      return
    }

    // substatusMap is derived from TMS_PORTAL's store via the selector.
    const { substatusMap } = selectSubstatusMaps(state)
    const fromLc = substatusMap[order.OrderStatus] || LIFECYCLES.ORDER_RECEIVED

    // Override warning when leaving a driver-controlled column in Today mode.
    if (
      dash.dateMode === 'today' &&
      DRIVER_CONTROLLED_LIFECYCLES.includes(fromLc as any) &&
      fromLc !== toLc
    ) {
      const ok = await payload.promptModal('overrideDriver', order)
      if (!ok) {
        dispatch(setOrders(dash.orders.slice()))
        return
      }
    }

    let patch: Record<string, any> = {}
    let result: any

    if (toLc === LIFECYCLES.ORDER_RECEIVED) {
      patch = {
        'fields.OrderStatus': 'Created',
        'fields.Vehicle': '',
        'fields.Driver': '',
      }
    } else if (toLc === LIFECYCLES.AT_DISPATCHING) {
      result = await payload.promptModal('assignVehicle', order, { priorStop: payload.priorStop ?? null })
      if (!result) {
        dispatch(setOrders(dash.orders.slice()))
        return
      }
      patch = {
        'fields.OrderStatus': 'Not Started',
        'fields.Vehicle': result.vehicle,
        'fields.Driver': result.driver || '',
      }
      if (result.startWindow) patch['fields.StartWindow'] = result.startWindow
      if (result.endWindow) patch['fields.EndWindow'] = result.endWindow
    } else if (toLc === LIFECYCLES.EN_ROUTE) {
      patch = { 'fields.OrderStatus': 'Started' }
    } else if (toLc === LIFECYCLES.COMPLETE) {
      result = await payload.promptModal('completeOrCancel', order)
      if (!result) {
        dispatch(setOrders(dash.orders.slice()))
        return
      }
      patch = { 'fields.OrderStatus': result.status } // 'Completed' or 'Cancelled'
    }

    try {
      const batch = writeBatch(db)
      const col = getProjectCollection('TMS_ORDERS')
      batch.update(doc(col, payload.orderId), patch)
      if (Array.isArray(result?.cascade)) {
        result.cascade
          .filter((c: any) => c.id && c.id !== payload.orderId)
          .forEach((c: any) => {
            batch.update(doc(col, c.id), { 'fields.StartWindow': c.startWindow, 'fields.EndWindow': c.endWindow })
          })
      }
      await batch.commit()
    } catch (err: any) {
      dispatch(setError(err?.message ?? 'Transition failed'))
      dispatch(setOrders(dash.orders.slice()))
    }
  }

/**
 * Driver-mode column drag: drop an order into another driver's column. Dropping
 * into "Unassigned" clears Vehicle+Driver directly; otherwise prompts the
 * assign-vehicle modal prefilled with the target driver.
 */
export const reassignToDriver =
  (payload: {
    orderId: string
    targetDriverName: string
    promptModal: PromptModal
    priorStop?: any
  }) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const state = getState()
    const dash: DashboardState = state.dashboard
    const order = dash.orders.find((o) => o.id === payload.orderId)
    if (!order) return
    const target = payload.targetDriverName

    if (target === 'Unassigned') {
      try {
        await updateDoc(doc(getProjectCollection('TMS_ORDERS'), payload.orderId), {
          'fields.Vehicle': '',
          'fields.Driver': '',
        })
      } catch (err: any) {
        dispatch(setError(err?.message ?? 'Reassign failed'))
        dispatch(setOrders(dash.orders.slice()))
      }
      return
    }

    // Driver columns are keyed by display name, but the assign modal seeds its
    // driver <select> from `driverUserName` (the options are keyed by driver id).
    // Resolve the name → id here, else the modal opens on "Unassigned" even though
    // the card was dropped on a driver's column. Region drivers first; fall back to
    // an order already assigned to that driver (columns also exist for drivers seen
    // on orders but outside the region list).
    const targetDriverId =
      selectRegionDriversDashboard(state).find(
        (d) => (d.label || '').split('***')[0] === target,
      )?.id
      || dash.orders.find((o) => o.Driver === target && !!o.driverUserName)?.driverUserName
      || ''

    const prefilledOrder = { ...order, Driver: target, driverUserName: targetDriverId }
    const result = await payload.promptModal('assignVehicle', prefilledOrder, { priorStop: payload.priorStop ?? null })
    if (!result) {
      dispatch(setOrders(dash.orders.slice()))
      return
    }

    const patch: Record<string, any> = {
      'fields.Vehicle': result.vehicle,
      'fields.Driver': result.driver || '',
    }
    if (result.startWindow) patch['fields.StartWindow'] = result.startWindow
    if (result.endWindow) patch['fields.EndWindow'] = result.endWindow

    try {
      const batch = writeBatch(db)
      const col = getProjectCollection('TMS_ORDERS')
      batch.update(doc(col, payload.orderId), patch)
      if (Array.isArray(result?.cascade)) {
        result.cascade
          .filter((c: any) => c.id && c.id !== payload.orderId)
          .forEach((c: any) => {
            batch.update(doc(col, c.id), { 'fields.StartWindow': c.startWindow, 'fields.EndWindow': c.endWindow })
          })
      }
      await batch.commit()
    } catch (err: any) {
      dispatch(setError(err?.message ?? 'Reassign failed'))
      dispatch(setOrders(dash.orders.slice()))
    }
  }

/**
 * Vehicle-mode column drag: drop an order into another vehicle's column. Dropping
 * into "Unassigned" clears Vehicle+Driver directly; otherwise prompts the
 * assign-vehicle modal prefilled with the target vehicle (mirrors reassignToDriver,
 * which prefills the target driver).
 */
export const reassignToVehicle =
  (payload: {
    orderId: string
    targetVehicleName: string
    promptModal: PromptModal
    priorStop?: any
  }) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const state = getState()
    const dash: DashboardState = state.dashboard
    const order = dash.orders.find((o) => o.id === payload.orderId)
    if (!order) return
    const target = payload.targetVehicleName

    if (target === 'Unassigned') {
      try {
        await updateDoc(doc(getProjectCollection('TMS_ORDERS'), payload.orderId), {
          'fields.Vehicle': '',
          'fields.Driver': '',
        })
      } catch (err: any) {
        dispatch(setError(err?.message ?? 'Reassign failed'))
        dispatch(setOrders(dash.orders.slice()))
      }
      return
    }

    const prefilledOrder = { ...order, Vehicle: target }
    const result = await payload.promptModal('assignVehicle', prefilledOrder, { priorStop: payload.priorStop ?? null })
    if (!result) {
      dispatch(setOrders(dash.orders.slice()))
      return
    }

    const patch: Record<string, any> = {
      'fields.Vehicle': result.vehicle,
      'fields.Driver': result.driver || '',
    }
    if (result.startWindow) patch['fields.StartWindow'] = result.startWindow
    if (result.endWindow) patch['fields.EndWindow'] = result.endWindow

    try {
      const batch = writeBatch(db)
      const col = getProjectCollection('TMS_ORDERS')
      batch.update(doc(col, payload.orderId), patch)
      if (Array.isArray(result?.cascade)) {
        result.cascade
          .filter((c: any) => c.id && c.id !== payload.orderId)
          .forEach((c: any) => {
            batch.update(doc(col, c.id), { 'fields.StartWindow': c.startWindow, 'fields.EndWindow': c.endWindow })
          })
      }
      await batch.commit()
    } catch (err: any) {
      dispatch(setError(err?.message ?? 'Reassign failed'))
      dispatch(setOrders(dash.orders.slice()))
    }
  }

/**
 * Today-mode reschedule: write the moved order's StartWindow/EndWindow plus its
 * driver-based cascade in one atomic batch. Rolls back to the current orders on error.
 */
export const rescheduleOrder =
  (payload: {
    orderId: string
    startWindow: Date
    endWindow: Date
    cascade?: Array<{ id: string; startWindow: Date; endWindow: Date }>
  }) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const dash: DashboardState = getState().dashboard
    try {
      const batch = writeBatch(db)
      const col = getProjectCollection('TMS_ORDERS')
      batch.update(doc(col, payload.orderId), {
        'fields.StartWindow': payload.startWindow,
        'fields.EndWindow': payload.endWindow,
      })
      // Invariant: the moved order carries its own authoritative time above; never
      // let a cascade entry re-write (clobber) it, and skip entries with no id.
      ;(payload.cascade || [])
        .filter((c) => c.id && c.id !== payload.orderId)
        .forEach((c) => {
          batch.update(doc(col, c.id), {
            'fields.StartWindow': c.startWindow,
            'fields.EndWindow': c.endWindow,
          })
        })
      await batch.commit()
    } catch (err: any) {
      dispatch(setError(err?.message ?? 'Reschedule failed'))
      dispatch(setOrders(dash.orders.slice()))
    }
  }

/**
 * Bulk-reassign vehicle/driver across the current bulkSelection. Prompts the
 * assign-vehicle modal in bulk mode, then writes Vehicle+Driver to each order
 * in one Firestore batch.
 */
export const bulkReassignVehicle =
  (payload: { promptModal: PromptModal }) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const state = getState()
    const dash: DashboardState = state.dashboard
    const ids = (dash.bulkSelection || []).slice()
    if (!ids.length) return

    const orders = ids
      .map((id) => dash.orders.find((o) => o.id === id))
      .filter(Boolean) as any[]
    // Prefill the modal from a shared value so an unchanged field is preserved,
    // not blanked. Empty when the selection spans different vehicles/drivers.
    const allShare = (get: (o: any) => any) => orders.every((o) => get(o) === get(orders[0] || {}))
    const sharedVehicle = allShare((o) => o.Vehicle) ? orders[0]?.Vehicle || '' : ''
    const sharedDriverUserName = allShare((o) => o.driverUserName) ? orders[0]?.driverUserName || '' : ''
    const synthetic = {
      bulkCount: ids.length,
      Vehicle: sharedVehicle,
      driverUserName: sharedDriverUserName,
      TimeZone: orders[0]?.TimeZone,
    }

    const result = await payload.promptModal('assignVehicle', synthetic)
    if (!result) return

    try {
      const batch = writeBatch(db)
      ids.forEach((id) => {
        const ref = doc(getProjectCollection('TMS_ORDERS'), id)
        // Always set the vehicle; only touch the driver when one was chosen, so a
        // vehicle-only reassign never silently clears the existing driver.
        const patch: Record<string, any> = { 'fields.Vehicle': result.vehicle }
        if (result.driver) patch['fields.Driver'] = result.driver
        batch.update(ref, patch)
      })
      await batch.commit()
      dispatch(setBulkSelection([]))
    } catch (err: any) {
      dispatch(setError(err?.message ?? 'Bulk reassign failed'))
    }
  }
